import type { ReactiveController, ReactiveControllerHost } from "lit";

export type PopoverSide = "top" | "bottom" | "left" | "right";
export type PopoverPlacement = PopoverSide | "auto";
export type PopoverAlign = "start" | "center" | "end";
export type MaxWidthMode = "cap" | "fill";
export type CloseReason = "api" | "toggle" | "escape" | "light-dismiss" | "group";
type ArrowAlign = "center" | "start" | "end";
type ScrollContainerMode = "auto" | "window";

/** Named animation presets; consumer selects by name. */
export type PopoverPreset =
  | "none"
  | "fade"
  | "scale"
  | "slide"
  | "iosMenu"
  | "iosSheet"
  | "materialMenu"
  | "materialSheet"
  | "floaty"
  | "soft"
  | "sharp"
  | "blurIn"
  | "blurFade"
  | "zoomFromAnchor"
  | "zoomFromCorner"
  | "flip3d"
  | "card3d"
  | "tilt3d"
  | "depth3d"
  | "rotate3d"
  | "springy"
  | "bouncy"
  | "glide"
  | "pop"
  | "drop"
  | "grow";

export type PopoverPresetConfig = {
  enterPx?: number;      // translate distance
  exitPx?: number;
  enterScale?: number;
  exitScale?: number;
  blurInPx?: number;
  blurOutPx?: number;
  perspectivePx?: number;
  rotateDeg?: number;    // 3d rotation magnitude
  tiltDeg?: number;      // 3d tilt magnitude
  origin?: "auto" | "anchor" | "center" | "corner";
  easingOpen?: string;
  easingClose?: string;
  enterDuration?: number;
  exitDuration?: number;
};

type PresetDef = {
  name: PopoverPreset;
  defaults: Required<PopoverPresetConfig>;
  getKeyframes: (
    args: {
      side: PopoverSide;
      phase: "open" | "close";
      cfg: Required<PopoverPresetConfig>;
      origin: string;
    }
  ) => Keyframe[];
};

export interface PopoverControllerOptions {
  // Required functions
  getAnchor: () => HTMLElement | null;
  getPopover: () => HTMLElement | null;

  // CSS variable names
  cssVarLeft?: string;
  cssVarTop?: string;
  cssVarWidth?: string;

  // Reactive property getters
  // (Preferred - automatically update when component properties change)
  getPlacement?: () => PopoverPlacement;
  getAlign?: () => PopoverAlign;
  getGap?: () => number;
  getPadding?: () => number;
  getMatchAnchorWidth?: () => boolean;
  getMaxWidthToViewport?: () => boolean;
  getMaxWidthMode?: () => MaxWidthMode;
  getFlip?: () => boolean;
  getFlipOrder?: () => PopoverSide[];
  getPreset?: () => PopoverPreset;
  getDuration?: () => number;
  getCloseDuration?: () => number;
  getAnimateReposition?: () => boolean;
  getRepositionMs?: () => number;
  getCloseOnEscape?: () => boolean;
  getRestoreFocusOnClose?: () => boolean;
  getCloseOnOutside?: () => boolean;
  getIgnoreOutsideSelector?: () => string;
  getIgnoreOutsideAttr?: () => string;

  // Static fallback values
  // (For backward compatibility or when values don't change)
  placement?: PopoverPlacement;
  align?: PopoverAlign;
  gap?: number;
  padding?: number;
  matchAnchorWidth?: boolean;
  maxWidthToViewport?: boolean;
  maxWidthMode?: MaxWidthMode;
  flip?: boolean;
  flipOrder?: PopoverSide[];
  preset?: PopoverPreset;
  duration?: number;
  closeDuration?: number;
  animateReposition?: boolean;
  repositionMs?: number;
  closeOnEscape?: boolean;
  restoreFocusOnClose?: boolean;
  closeOnOutside?: boolean;
  ignoreOutsideSelector?: string;
  ignoreOutsideAttr?: string;

  // Scroll and focus
  focusOnOpen?: () => void;
  scrollContainer?: ScrollContainerMode | HTMLElement;

  // Group behavior
  groupEventName?: string;
  groupId?: unknown;

  // Callbacks
  onOpenChange?: (open: boolean, meta: { reason: CloseReason }) => void;
  /** Focus target on close; defaults to {@link getAnchor} when omitted. */
  getFocusTarget?: () => HTMLElement | null;

  // Other options
  sideAttr?: string;
  respectReducedMotion?: boolean;
}

/**
 * PopoverController with Reactive Getter Bindings
 * - Automatically reads latest values from host component via getter functions
 * - No manual updates needed when host properties change
 * - WAAPI preset animations with side-aware enter/exit
 * - Smooth reposition animation on flip/move
 */
export class PopoverController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _open = false;

  private raf = 0;

  private anchorRO?: ResizeObserver;
  private popoverRO?: ResizeObserver;

  private boundScrollParents: Array<{ el: EventTarget; fn: EventListener }> = [];
  private visualViewport?: VisualViewport;
  /** When true, global listeners and observers are attached (deferred until first open for perf with many instances). */
  private _listenersAttached = false;

  private lastCloseReason: CloseReason = "toggle";
  private lastFocusedEl: Element | null = null;

  private hasEverOpened = false;
  private _skipNextReposition = false;

  private lastMeta: { place: PopoverSide; arrowAlign: ArrowAlign } = {
    place: "bottom",
    arrowAlign: "center",
  };

  private _phase: "idle" | "opening" | "open" | "closing" = "idle";
  private presetAnim?: Animation;
  private moveAnim?: Animation;

  // Helper to create getters with fallback to static values
  private static createGetter<T>(
    getter: (() => T) | undefined,
    staticValue: T | undefined,
    defaultValue: T
  ): () => T {
    return getter || (() => staticValue ?? defaultValue);
  }

  private opts: {
    // Required functions
    getAnchor: () => HTMLElement | null;
    getPopover: () => HTMLElement | null;

    // Reactive property getters
    getPlacement: () => PopoverPlacement;
    getAlign: () => PopoverAlign;
    getGap: () => number;
    getPadding: () => number;
    getMatchAnchorWidth: () => boolean;
    getMaxWidthToViewport: () => boolean;
    getMaxWidthMode: () => MaxWidthMode;
    getFlip: () => boolean;
    getFlipOrder: () => PopoverSide[];
    getPreset: () => PopoverPreset;
    getDuration: () => number;
    getCloseDuration: () => number;
    getAnimateReposition: () => boolean;
    getRepositionMs: () => number;
    getCloseOnEscape: () => boolean;
    getRestoreFocusOnClose: () => boolean;
    getCloseOnOutside: () => boolean;
    getIgnoreOutsideSelector: () => string;
    getIgnoreOutsideAttr: () => string;

    // Other options
    cssVarLeft: string;
    cssVarTop: string;
    cssVarWidth: string;
    focusOnOpen?: () => void;
    scrollContainer: ScrollContainerMode | HTMLElement;
    groupEventName?: string;
    groupId?: unknown;
    onOpenChange?: (open: boolean, meta: { reason: CloseReason }) => void;
    getFocusTarget?: () => HTMLElement | null;
    sideAttr: string;
    respectReducedMotion: boolean;
  };

  constructor(host: ReactiveControllerHost, options: PopoverControllerOptions) {
    this.host = host;
    host.addController(this);

    // Initialize getters with fallback values
    const dur = options.duration ?? 220;
    const closeDur = options.closeDuration ?? Math.min(180, dur);

    this.opts = {
      // Required functions
      getAnchor: options.getAnchor,
      getPopover: options.getPopover,

      // Reactive property getters
      getPlacement: PopoverController.createGetter(
        options.getPlacement,
        options.placement,
        "auto"
      ),
      getAlign: PopoverController.createGetter(
        options.getAlign,
        options.align,
        "start"
      ),
      getGap: PopoverController.createGetter(
        options.getGap,
        options.gap,
        6
      ),
      getPadding: PopoverController.createGetter(
        options.getPadding,
        options.padding,
        8
      ),
      getMatchAnchorWidth: PopoverController.createGetter(
        options.getMatchAnchorWidth,
        options.matchAnchorWidth,
        true
      ),
      getMaxWidthToViewport: PopoverController.createGetter(
        options.getMaxWidthToViewport,
        options.maxWidthToViewport,
        true
      ),
      getMaxWidthMode: PopoverController.createGetter(
        options.getMaxWidthMode,
        options.maxWidthMode,
        "cap"
      ),
      getFlip: PopoverController.createGetter(
        options.getFlip,
        options.flip,
        true
      ),
      getFlipOrder: PopoverController.createGetter(
        options.getFlipOrder,
        options.flipOrder,
        []
      ),
      getPreset: PopoverController.createGetter(
        options.getPreset,
        options.preset,
        "slide"
      ),
      getDuration: PopoverController.createGetter(
        options.getDuration,
        options.duration,
        dur
      ),
      getCloseDuration: PopoverController.createGetter(
        options.getCloseDuration,
        options.closeDuration,
        closeDur
      ),
      getAnimateReposition: PopoverController.createGetter(
        options.getAnimateReposition,
        options.animateReposition,
        true
      ),
      getRepositionMs: PopoverController.createGetter(
        options.getRepositionMs,
        options.repositionMs,
        160
      ),
      getCloseOnEscape: PopoverController.createGetter(
        options.getCloseOnEscape,
        options.closeOnEscape,
        true
      ),
      getCloseOnOutside: PopoverController.createGetter(
        options.getCloseOnOutside,
        options.closeOnOutside,
        false
      ),

      getIgnoreOutsideSelector: PopoverController.createGetter(
        options.getIgnoreOutsideSelector,
        options.ignoreOutsideSelector,
        "" // none by default
      ),

      getIgnoreOutsideAttr: PopoverController.createGetter(
        options.getIgnoreOutsideAttr,
        options.ignoreOutsideAttr,
        "data-popover-ignore-outside"
      ),
      getRestoreFocusOnClose: PopoverController.createGetter(
        options.getRestoreFocusOnClose,
        options.restoreFocusOnClose,
        true
      ),

      // Other options
      cssVarLeft: options.cssVarLeft ?? "--sky-pop-left",
      cssVarTop: options.cssVarTop ?? "--sky-pop-top",
      cssVarWidth: options.cssVarWidth ?? "--sky-pop-width",
      focusOnOpen: options.focusOnOpen,
      scrollContainer: options.scrollContainer ?? "auto",
      groupEventName: options.groupEventName,
      groupId: options.groupId,
      onOpenChange: options.onOpenChange,
      getFocusTarget: options.getFocusTarget,
      sideAttr: options.sideAttr ?? "data-side",
      respectReducedMotion: options.respectReducedMotion ?? true,
    };
  }

  get open() {
    return this._open;
  }

  get meta() {
    return this.lastMeta;
  }

  hostConnected() {
    if (typeof window === "undefined") return;
    this.visualViewport = window.visualViewport ?? undefined;
    // Defer global listeners and observers until first open (avoids N×listeners with many instances, e.g. combobox in every flow node).
  }

  hostDisconnected() {
    this.detachGlobalListenersIfAttached();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /** Attach window/document listeners and observers. Called on first open. */
  private attachGlobalListeners() {
    if (typeof window === "undefined" || this._listenersAttached) return;
    this._listenersAttached = true;

    if (this.visualViewport) {
      this.visualViewport.addEventListener("resize", this.onViewportMove, { passive: true });
      this.visualViewport.addEventListener("scroll", this.onViewportMove, { passive: true });
    }
    window.addEventListener("resize", this.onViewportMove, { passive: true });
    window.addEventListener("scroll", this.onViewportMove, { passive: true, capture: true });

    if (this.opts.groupEventName) {
      window.addEventListener(this.opts.groupEventName, this.onGroupOpen as EventListener);
    }
    if (this.opts.getCloseOnEscape()) {
      window.addEventListener("keydown", this.onKeydown, true);
    }
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);

    this.setupObserversAndScrollParents();
  }

  /** Remove window/document listeners and observers. Called on close and on hostDisconnected. */
  private detachGlobalListenersIfAttached() {
    if (!this._listenersAttached) return;
    this._listenersAttached = false;

    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.onViewportMove);
      window.removeEventListener("scroll", this.onViewportMove, true);
      if (this.visualViewport) {
        this.visualViewport.removeEventListener("resize", this.onViewportMove);
        this.visualViewport.removeEventListener("scroll", this.onViewportMove);
      }
      if (this.opts.groupEventName) {
        window.removeEventListener(this.opts.groupEventName, this.onGroupOpen as EventListener);
      }
      window.removeEventListener("keydown", this.onKeydown, true);
    }
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);

    this.teardownScrollParents();
    this.anchorRO?.disconnect();
    this.anchorRO = undefined;
    this.popoverRO?.disconnect();
    this.popoverRO = undefined;
  }

  refreshTargets() {
    if (this._listenersAttached) this.setupObserversAndScrollParents();
    if (this._open) this.position();
  }

  /** Wire to popover element: @toggle=${controller.onToggle} */
  onToggle = (e: Event) => {
    if (this.lastCloseReason === "light-dismiss") {
      this.lastCloseReason = "toggle";
      return;
    }
    const pop = this.opts.getPopover();
    const ev = e as any;

    let isOpen: boolean | null = null;
    if (typeof ev?.newState === "string") isOpen = ev.newState === "open";
    if (isOpen === null && pop) isOpen = pop.matches?.(":popover-open") ?? false;
    if (isOpen === null) return;

    if (!isOpen) {
      this._open = false;
      this.detachGlobalListenersIfAttached();
      this.opts.onOpenChange?.(false, { reason: this.lastCloseReason });
      if (this.opts.getRestoreFocusOnClose()) this.restoreFocus();
      return;
    }

    this._open = true;
    this.attachGlobalListeners();
    this.opts.onOpenChange?.(true, { reason: "toggle" });

    this.position();
    this.raf = requestAnimationFrame(() => this.opts.focusOnOpen?.());
  };

  toggle() {
    this._open ? this.closePopover("api") : this.openPopover();
  }

  openPopover() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    const pop = this.opts.getPopover();
    if (!pop) return;

    if (typeof window !== "undefined" && this.opts.groupEventName) {
      window.dispatchEvent(
        new CustomEvent(this.opts.groupEventName, {
          detail: { id: this.opts.groupId ?? this.host },
        }),
      );
    }

    this.captureFocusBeforeOpen();
    this.ensurePopoverMarginZero();

    this._open = true;
    this.attachGlobalListeners();
    this.opts.onOpenChange?.(true, { reason: "api" });

    // Hide until positioned to avoid first-paint flicker.
    const prevVisibility = pop.style.visibility;
    pop.style.visibility = "hidden";

    (pop as any).showPopover?.();
    const wasEverOpen = this.hasEverOpened;
    this.hasEverOpened = true;

    // later: pass this to position or set a field
    this._skipNextReposition = !wasEverOpen;

    // 1) compute and apply css-var position ASAP
    this.position();

    // 2) force style/layout flush so left/top vars take effect before animation
    pop.getBoundingClientRect();

    // 3) now animate + reveal
    const side = this.lastMeta.place;
    this.setSideAttr(side);

    // reveal on next frame so hidden state doesn't get captured mid-animation
    this.raf = requestAnimationFrame(() => {
      pop.style.visibility = prevVisibility || "visible";

      this.playPreset(pop, side, "open").then(() => this.opts.focusOnOpen?.());
    });
  }

  private onDocumentPointerDown = (e: PointerEvent) => {
    if (!this._open) return;
    if (!this.opts.getCloseOnOutside()) return;

    const pop = this.opts.getPopover();
    const anchor = this.opts.getAnchor();
    if (!pop || !anchor) return;

    // ignore non-primary clicks
    if (typeof e.button === "number" && e.button !== 0) return;

    const path = (e.composedPath?.() ?? []) as EventTarget[];

    // If click is on anchor or inside popover => not outside
    const insidePopover = path.includes(pop);
    const insideAnchor = path.includes(anchor);
    if (insidePopover || insideAnchor) return;

    // Nested popovers (combobox lists, datepicker calendars, nested menus) live in
    // the top layer outside this popover. Treat those clicks as inside nested UI.
    for (const node of path) {
      if (!(node instanceof Element)) continue;
      if (typeof (node as Element).matches === "function") {
        if ((node as Element).matches(":popover-open")) return;
      }
      const openAncestor = (node as Element).closest?.(":popover-open");
      if (openAncestor && openAncestor !== pop) return;
    }

    // Ignore scrollbar clicks (rare but real)
    const x = e.clientX, y = e.clientY;
    if (x >= window.innerWidth || y >= window.innerHeight) return;

    // Ignore zones: attr + selector
    const ignoreAttr = this.opts.getIgnoreOutsideAttr();
    const ignoreSel = (this.opts.getIgnoreOutsideSelector() || "").trim();

    for (const node of path) {
      if (!(node instanceof HTMLElement)) continue;

      // attr ignore
      if (ignoreAttr && node.hasAttribute(ignoreAttr)) return;

      // selector ignore
      if (ignoreSel) {
        // If any node in the path matches OR is inside an element that matches
        if (node.matches(ignoreSel)) return;
        if (node.closest(ignoreSel)) return;
      }
    }

    this.closePopover("light-dismiss");
  };



  async closePopover(reason: CloseReason = "api") {
    const pop = this.opts.getPopover();
    if (!pop) return;

    this.lastCloseReason = reason;

    if (!this._open) {
      (pop as any).hidePopover?.();
      return;
    }

    this._open = false;
    // Restore focus before onOpenChange so the host can re-render with aria-hidden="true"
    // without violating "aria-hidden must not hide a focused element" (focus is already on anchor).
    if (this.opts.getRestoreFocusOnClose()) this.restoreFocus();
    this.detachGlobalListenersIfAttached();
    this.opts.onOpenChange?.(false, { reason });

    const side = this.lastMeta.place;
    this.setSideAttr(side);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.moveAnim?.cancel();
    await this.playPreset(pop, side, "close");

    (pop as any).hidePopover?.();
  }

  private normalizeFlipOrder(order?: PopoverSide[]) {
    const base: PopoverSide[] = ["bottom", "top", "right", "left"];
    if (!order || !order.length) return base;

    const valid = new Set<PopoverSide>(["top", "bottom", "left", "right"]);
    const out: PopoverSide[] = [];
    for (const s of order) if (valid.has(s) && !out.includes(s)) out.push(s);
    for (const s of base) if (!out.includes(s)) out.push(s);
    return out;
  }

  private defaultOrderFromPlacement(p: PopoverPlacement): PopoverSide[] {
    switch (p) {
      case "top": return ["top", "bottom", "right", "left"];
      case "bottom": return ["bottom", "top", "right", "left"];
      case "left": return ["left", "right", "bottom", "top"];
      case "right": return ["right", "left", "bottom", "top"];
      default: return ["bottom", "top", "right", "left"];
    }
  }

  position() {
    if (typeof window === "undefined") return;

    const anchor = this.opts.getAnchor();
    const pop = this.opts.getPopover();
    if (!anchor || !pop) return;

    this.ensurePopoverMarginZero();

    const isActuallyVisible =
      (pop as any).matches?.(":popover-open") && pop.offsetParent !== null;

    const before = (this._open && isActuallyVisible) ? pop.getBoundingClientRect() : null;

    const pad = this.opts.getPadding();
    const gap = this.opts.getGap();

    const a = anchor.getBoundingClientRect();

    const vv = window.visualViewport ?? null;
    const vw = vv?.width ?? window.innerWidth;
    const vh = vv?.height ?? window.innerHeight;

    const offsetX = vv?.offsetLeft ?? 0;
    const offsetY = vv?.offsetTop ?? 0;

    if (this.opts.getMaxWidthToViewport()) {
      const maxW = Math.max(0, vw - pad * 2);
      pop.style.maxWidth = `${maxW}px`;
      pop.style.boxSizing = "border-box";
    }

    const pr = pop.getBoundingClientRect();
    const popW = pr.width || 220;
    const popH = pr.height || 50;

    const space = {
      top: a.top - gap - offsetY,
      bottom: vh - (a.bottom - offsetY) - gap,
      left: a.left - gap - offsetX,
      right: vw - (a.right - offsetX) - gap,
    };

    const fits = (side: PopoverSide) => {
      if (side === "top") return space.top >= popH;
      if (side === "bottom") return space.bottom >= popH;
      if (side === "left") return space.left >= popW;
      return space.right >= popW;
    };

    const requested = this.opts.getPlacement();
    const flipEnabled = this.opts.getFlip();

    const baseOrder =
      this.opts.getFlipOrder()?.length
        ? this.normalizeFlipOrder(this.opts.getFlipOrder())
        : this.normalizeFlipOrder(this.defaultOrderFromPlacement(requested));

    let place: PopoverSide;

    if (requested === "auto") {
      const firstFit = baseOrder.find(fits);
      place =
        firstFit ??
        (baseOrder
          .map((s) => [s, space[s]] as const)
          .sort((x, y) => y[1] - x[1])[0][0]);
    } else if (!flipEnabled) {
      place = requested as PopoverSide;
    } else {
      const req = requested as PopoverSide;
      const prioritized = baseOrder.includes(req)
        ? [req, ...baseOrder.filter((s) => s !== req)]
        : [req, ...baseOrder];

      const firstFit = prioritized.find(fits);
      place =
        firstFit ??
        (prioritized
          .map((s) => [s, space[s]] as const)
          .sort((x, y) => y[1] - x[1])[0][0]);
    }

    let top = 0;
    let left = 0;
    let arrowAlign: ArrowAlign = "center";

    const isRTL = getComputedStyle(anchor).direction === "rtl";
    let align = this.opts.getAlign();
    if (isRTL) {
      if (align === "start") align = "end";
      else if (align === "end") align = "start";
    }

    const ax1 = a.left - offsetX;
    const ax2 = a.right - offsetX;
    const ay1 = a.top - offsetY;
    const ay2 = a.bottom - offsetY;
    const aw = a.width;
    const ah = a.height;

    if (place === "top" || place === "bottom") {
      if (align === "start") left = ax1;
      else if (align === "end") left = ax2 - popW;
      else left = ax1 + aw / 2 - popW / 2;

      top = place === "top" ? ay1 - popH - gap : ay2 + gap;

      const clampedLeft = Math.max(pad, Math.min(vw - popW - pad, left));
      if (clampedLeft !== left) arrowAlign = clampedLeft <= pad + 0.5 ? "start" : "end";
      left = clampedLeft;

      top = Math.max(pad, Math.min(vh - popH - pad, top));
    } else {
      left = place === "left" ? ax1 - popW - gap : ax2 + gap;
      top = ay1 + ah / 2 - popH / 2;

      const clampedTop = Math.max(pad, Math.min(vh - popH - pad, top));
      if (clampedTop !== top) arrowAlign = clampedTop <= pad + 0.5 ? "start" : "end";
      top = clampedTop;

      left = Math.max(pad, Math.min(vw - popW - pad, left));
    }

    left += offsetX;
    top += offsetY;

    const hostEl = this.host as unknown as HTMLElement;

    if (this.opts.getMatchAnchorWidth()) {
      const maxW = Math.max(0, vw - pad * 2);
      const width = Math.min(a.width, maxW);
      hostEl.style.setProperty(this.opts.cssVarWidth, `${width}px`);
    } else {
      hostEl.style.removeProperty(this.opts.cssVarWidth);
    }

    hostEl.style.setProperty(this.opts.cssVarLeft, `${left}px`);
    hostEl.style.setProperty(this.opts.cssVarTop, `${top}px`);
    // Top-layer popovers do not reliably inherit custom props from the host.
    pop.style.setProperty(this.opts.cssVarLeft, `${left}px`);
    pop.style.setProperty(this.opts.cssVarTop, `${top}px`);
    if (this.opts.getMatchAnchorWidth()) {
      const width = hostEl.style.getPropertyValue(this.opts.cssVarWidth);
      if (width) pop.style.setProperty(this.opts.cssVarWidth, width);
    } else {
      pop.style.removeProperty(this.opts.cssVarWidth);
    }

    const prevPlace = this.lastMeta.place;
    this.lastMeta = { place, arrowAlign };
    this.setSideAttr(place);

    const canReposition =
      this._open &&
      this.opts.getAnimateReposition() &&
      before &&
      !this._skipNextReposition &&
      pop.matches?.(":popover-open") &&
      isActuallyVisible &&
      this._phase === "open" &&
      !this.presetAnim; // extra safety

    if (canReposition) {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        const after = pop.getBoundingClientRect();
        this.animateMove(pop, before, after);
      });
    }

    this._skipNextReposition = false;
  }

  private vec(side: PopoverSide, v: number) {
    if (side === "top") return { x: 0, y: +v };
    if (side === "bottom") return { x: 0, y: -v };
    if (side === "left") return { x: +v, y: 0 };
    return { x: -v, y: 0 };
  }

  private prefersReducedMotion() {
    if (!this.opts.respectReducedMotion) return false;
    return typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }

  /** sets transformOrigin according to preset + side */
  private computeOrigin(side: PopoverSide, mode: PopoverPresetConfig["origin"]) {
    if (mode === "center") return "50% 50%";
    if (mode === "corner") {
      // corner opposite of side looks best
      if (side === "top") return "50% 100%";
      if (side === "bottom") return "50% 0%";
      if (side === "left") return "100% 50%";
      return "0% 50%";
    }
    if (mode === "anchor") {
      // you can refine this using anchor rect; for now side-based is good
      if (side === "top") return "50% 100%";
      if (side === "bottom") return "50% 0%";
      if (side === "left") return "100% 50%";
      return "0% 50%";
    }
    // auto
    if (side === "top") return "50% 100%";
    if (side === "bottom") return "50% 0%";
    if (side === "left") return "100% 50%";
    return "0% 50%";
  }

  private kf(opacity: number, transform: string, filter?: string): Keyframe {
    const out: any = { opacity, transform };
    if (filter) out.filter = filter;
    return out as Keyframe;
  }

  // Preset registry
  private presetRegistry(): Record<PopoverPreset, PresetDef> {

    const fade: PresetDef = {
      name: "fade",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.4, 0, 0.2, 1)",
        easingClose: "cubic-bezier(0.4, 0, 0.2, 1)",
        enterDuration: 180, exitDuration: 150,
      },
      getKeyframes: ({ phase }) =>
        phase === "open"
          ? [{ opacity: 0 }, { opacity: 1 }]
          : [{ opacity: 1 }, { opacity: 0 }],
    };

    const scale: PresetDef = {
      name: "scale",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 0.9, exitScale: 0.95,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "corner",
        easingOpen: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        easingClose: "cubic-bezier(0.36, 0, 0.66, -0.56)",
        enterDuration: 200, exitDuration: 180,
      },
      getKeyframes: ({ phase, cfg }) =>
        phase === "open"
          ? [this.kf(0, `scale(${cfg.enterScale})`), this.kf(1, "scale(1)")]
          : [this.kf(1, "scale(1)"), this.kf(0, `scale(${cfg.exitScale})`)],
    };

    const slide: PresetDef = {
      name: "slide",
      defaults: {
        enterPx: 12, exitPx: 8, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "auto",
        easingOpen: "cubic-bezier(0.23, 1, 0.32, 1)",
        easingClose: "cubic-bezier(0.5, 0, 0.75, 0)",
        enterDuration: 250, exitDuration: 220,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px, 0)`), this.kf(1, "translate3d(0,0,0)")]
          : [this.kf(1, "translate3d(0,0,0)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px, 0)`)];
      },
    };

    const iosMenu: PresetDef = {
      name: "iosMenu",
      defaults: {
        enterPx: 12, exitPx: 8, enterScale: 0.98, exitScale: 0.99,
        blurInPx: 6, blurOutPx: 3, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "auto",
        easingOpen: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        easingClose: "cubic-bezier(0.55, 0.085, 0.68, 0.53)",
        enterDuration: 250, exitDuration: 220,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [
            this.kf(0, `translate3d(${a.x}px, ${a.y}px, 0) scale(${cfg.enterScale})`, `blur(${cfg.blurInPx}px)`),
            this.kf(1, "translate3d(0,0,0) scale(1)", "blur(0px)"),
          ]
          : [
            this.kf(1, "translate3d(0,0,0) scale(1)", "blur(0px)"),
            this.kf(0, `translate3d(${b.x}px, ${b.y}px, 0) scale(${cfg.exitScale})`, `blur(${cfg.blurOutPx}px)`),
          ];
      },
    };

    const iosSheet: PresetDef = {
      name: "iosSheet",
      defaults: {
        enterPx: 16, exitPx: 12, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "auto",
        easingOpen: "cubic-bezier(0.36, 0, 0.66, -0.56)",
        easingClose: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        enterDuration: 200, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px,0)`), this.kf(1, "translate3d(0,0,0)")]
          : [this.kf(1, "translate3d(0,0,0)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px,0)`)];
      },
    };

    const materialMenu: PresetDef = {
      name: "materialMenu",
      defaults: {
        enterPx: 10, exitPx: 6, enterScale: 0.96, exitScale: 0.98,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "corner",
        easingOpen: "cubic-bezier(0.2, 0, 0, 1)",
        easingClose: "cubic-bezier(0.4, 0, 1, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [
            this.kf(0, `translate3d(${a.x}px, ${a.y}px,0) scale(${cfg.enterScale})`),
            this.kf(1, "translate3d(0,0,0) scale(1)"),
          ]
          : [
            this.kf(1, "translate3d(0,0,0) scale(1)"),
            this.kf(0, `translate3d(${b.x}px, ${b.y}px,0) scale(${cfg.exitScale})`),
          ];
      },
    };

    const materialSheet: PresetDef = {
      name: "materialSheet",
      defaults: {
        enterPx: 18, exitPx: 14, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "auto",
        easingOpen: "cubic-bezier(0.05, 0.7, 0.1, 1)",
        easingClose: "cubic-bezier(0.3, 0, 1, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px,0)`), this.kf(1, "translate3d(0,0,0)")]
          : [this.kf(1, "translate3d(0,0,0)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px,0)`)];
      },
    };

    const floaty: PresetDef = {
      name: "floaty",
      defaults: {
        enterPx: 14, exitPx: 10, enterScale: 0.985, exitScale: 0.99,
        blurInPx: 10, blurOutPx: 6, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        easingClose: "cubic-bezier(0.55, 0.06, 0.68, 0.19)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [
            this.kf(0, `translate3d(${a.x}px, ${a.y}px,0) scale(${cfg.enterScale})`, `blur(${cfg.blurInPx}px)`),
            this.kf(1, "translate3d(0,0,0) scale(1)", "blur(0px)"),
          ]
          : [
            this.kf(1, "translate3d(0,0,0) scale(1)", "blur(0px)"),
            this.kf(0, `translate3d(${b.x}px, ${b.y}px,0) scale(${cfg.exitScale})`, `blur(${cfg.blurOutPx}px)`),
          ];
      },
    };

    const soft: PresetDef = {
      name: "soft",
      defaults: {
        enterPx: 10, exitPx: 8, enterScale: 0.97, exitScale: 0.98,
        blurInPx: 4, blurOutPx: 2, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.33, 1, 0.68, 1)",
        easingClose: "cubic-bezier(0.32, 0, 0.67, 0)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px,0) scale(${cfg.enterScale})`, `blur(${cfg.blurInPx}px)`), this.kf(1, "translate3d(0,0,0) scale(1)", "blur(0px)")]
          : [this.kf(1, "translate3d(0,0,0) scale(1)", "blur(0px)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px,0) scale(${cfg.exitScale})`, `blur(${cfg.blurOutPx}px)`)];
      },
    };

    const sharp: PresetDef = {
      name: "sharp",
      defaults: {
        enterPx: 8, exitPx: 6, enterScale: 0.98, exitScale: 0.99,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "corner",
        easingOpen: "cubic-bezier(0.12, 0.9, 0.2, 1)",
        easingClose: "cubic-bezier(0.4, 0, 1, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px,0) scale(${cfg.enterScale})`), this.kf(1, "translate3d(0,0,0) scale(1)")]
          : [this.kf(1, "translate3d(0,0,0) scale(1)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px,0) scale(${cfg.exitScale})`)];
      },
    };

    const blurIn: PresetDef = {
      name: "blurIn",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 1, exitScale: 1,
        blurInPx: 12, blurOutPx: 8, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.5, 0, 0.75, 0)",
        easingClose: "cubic-bezier(0.33, 0, 0.67, 0)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, cfg }) =>
        phase === "open"
          ? [this.kf(0, "translate3d(0,0,0)", `blur(${cfg.blurInPx}px)`), this.kf(1, "translate3d(0,0,0)", "blur(0px)")]
          : [this.kf(1, "translate3d(0,0,0)", "blur(0px)"), this.kf(0, "translate3d(0,0,0)", `blur(${cfg.blurOutPx}px)`)],
    };

    const blurFade: PresetDef = {
      name: "blurFade",
      defaults: {
        enterPx: 10, exitPx: 8, enterScale: 0.99, exitScale: 0.99,
        blurInPx: 10, blurOutPx: 6, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.45, 0, 0.55, 1)",
        easingClose: "cubic-bezier(0.3, 0, 0.7, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px,0)`, `blur(${cfg.blurInPx}px)`), this.kf(1, "translate3d(0,0,0)", "blur(0px)")]
          : [this.kf(1, "translate3d(0,0,0)", "blur(0px)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px,0)`, `blur(${cfg.blurOutPx}px)`)];
      },
    };

    const zoomFromAnchor: PresetDef = {
      name: "zoomFromAnchor",
      defaults: {
        enterPx: 6, exitPx: 4, enterScale: 0.92, exitScale: 0.96,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "anchor",
        easingOpen: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        easingClose: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const a = this.vec(side, cfg.enterPx);
        const b = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${a.x}px, ${a.y}px,0) scale(${cfg.enterScale})`), this.kf(1, "translate3d(0,0,0) scale(1)")]
          : [this.kf(1, "translate3d(0,0,0) scale(1)"), this.kf(0, `translate3d(${b.x}px, ${b.y}px,0) scale(${cfg.exitScale})`)];
      },
    };

    const zoomFromCorner: PresetDef = {
      name: "zoomFromCorner",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 0.88, exitScale: 0.94,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "corner",
        easingOpen: "cubic-bezier(0.18, 0.89, 0.32, 1.28)",
        easingClose: "cubic-bezier(0.6, -0.28, 0.74, 0.05)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, cfg }) =>
        phase === "open"
          ? [this.kf(0, `scale(${cfg.enterScale})`), this.kf(1, "scale(1)")]
          : [this.kf(1, "scale(1)"), this.kf(0, `scale(${cfg.exitScale})`)],
    };

    const flip3d: PresetDef = {
      name: "flip3d",
      defaults: {
        enterPx: 8, exitPx: 6, enterScale: 0.98, exitScale: 0.99,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 14, tiltDeg: 8, origin: "anchor",
        easingOpen: "cubic-bezier(0.5, 0, 0.75, 0)",
        easingClose: "cubic-bezier(0.33, 0, 0.67, 0)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg, origin }) => {
        const v = this.vec(side, cfg.enterPx);
        const axis = (side === "left" || side === "right") ? "Y" : "X";
        const sign = (side === "top" || side === "left") ? 1 : -1;
        const r = cfg.rotateDeg * sign;

        if (phase === "open") {
          return [
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${v.x}px, ${v.y}px, 0) rotate${axis}(${r}deg) scale(${cfg.enterScale})`),
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotate${axis}(0deg) scale(1)`),
          ];
        }
        const v2 = this.vec(side, cfg.exitPx);
        return [
          this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotate${axis}(0deg) scale(1)`),
          this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${v2.x}px, ${v2.y}px, 0) rotate${axis}(${r * 0.7}deg) scale(${cfg.exitScale})`),
        ];
      },
    };

    const card3d: PresetDef = {
      name: "card3d",
      defaults: {
        enterPx: 10, exitPx: 8, enterScale: 0.96, exitScale: 0.98,
        blurInPx: 6, blurOutPx: 3, perspectivePx: 1000,
        rotateDeg: 10, tiltDeg: 10, origin: "anchor",
        easingOpen: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        easingClose: "cubic-bezier(0.55, 0.085, 0.68, 0.53)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        const axis = (side === "left" || side === "right") ? "Y" : "X";
        const sign = (side === "top" || side === "left") ? 1 : -1;
        const t = cfg.tiltDeg * sign;

        return phase === "open"
          ? [
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${v.x}px, ${v.y}px, 0) rotate${axis}(${t}deg) scale(${cfg.enterScale})`, `blur(${cfg.blurInPx}px)`),
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotate${axis}(0deg) scale(1)`, "blur(0px)"),
          ]
          : [
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotate${axis}(0deg) scale(1)`, "blur(0px)"),
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${this.vec(side, cfg.exitPx).x}px, ${this.vec(side, cfg.exitPx).y}px, 0) rotate${axis}(${t * 0.7}deg) scale(${cfg.exitScale})`, `blur(${cfg.blurOutPx}px)`),
          ];
      },
    };

    const tilt3d: PresetDef = {
      name: "tilt3d",
      defaults: {
        enterPx: 8, exitPx: 6, enterScale: 0.98, exitScale: 0.99,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 12, origin: "corner",
        easingOpen: "cubic-bezier(0.23, 1, 0.32, 1)",
        easingClose: "cubic-bezier(0.5, 0, 0.75, 0)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        const rx = (side === "top" ? cfg.tiltDeg : side === "bottom" ? -cfg.tiltDeg : 0);
        const ry = (side === "left" ? cfg.tiltDeg : side === "right" ? -cfg.tiltDeg : 0);

        return phase === "open"
          ? [
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${v.x}px, ${v.y}px, 0) rotateX(${rx}deg) rotateY(${ry}deg) scale(${cfg.enterScale})`),
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotateX(0deg) rotateY(0deg) scale(1)`),
          ]
          : [
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotateX(0deg) rotateY(0deg) scale(1)`),
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${this.vec(side, cfg.exitPx).x}px, ${this.vec(side, cfg.exitPx).y}px, 0) rotateX(${rx * 0.6}deg) rotateY(${ry * 0.6}deg) scale(${cfg.exitScale})`),
          ];
      },
    };

    const depth3d: PresetDef = {
      name: "depth3d",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 0.9, exitScale: 0.94,
        blurInPx: 8, blurOutPx: 5, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        easingClose: "cubic-bezier(0.55, 0.06, 0.68, 0.19)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, cfg }) =>
        phase === "open"
          ? [
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(0,0,-40px) scale(${cfg.enterScale})`, `blur(${cfg.blurInPx}px)`),
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) scale(1)`, "blur(0px)"),
          ]
          : [
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) scale(1)`, "blur(0px)"),
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(0,0,-30px) scale(${cfg.exitScale})`, `blur(${cfg.blurOutPx}px)`),
          ],
    };

    const rotate3d: PresetDef = {
      name: "rotate3d",
      defaults: {
        enterPx: 8, exitPx: 6, enterScale: 0.98, exitScale: 0.99,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 12, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.36, 0, 0.66, -0.56)",
        easingClose: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        const sign = (side === "top" || side === "left") ? 1 : -1;
        const r = cfg.rotateDeg * sign;

        return phase === "open"
          ? [
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${v.x}px, ${v.y}px, 0) rotateZ(${r}deg) scale(${cfg.enterScale})`),
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotateZ(0deg) scale(1)`),
          ]
          : [
            this.kf(1, `perspective(${cfg.perspectivePx}px) translate3d(0,0,0) rotateZ(0deg) scale(1)`),
            this.kf(0, `perspective(${cfg.perspectivePx}px) translate3d(${this.vec(side, cfg.exitPx).x}px, ${this.vec(side, cfg.exitPx).y}px, 0) rotateZ(${r * 0.7}deg) scale(${cfg.exitScale})`),
          ];
      },
    };

    const springy: PresetDef = {
      name: "springy",
      defaults: {
        enterPx: 12, exitPx: 8, enterScale: 0.94, exitScale: 0.98,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "anchor",
        easingOpen: "cubic-bezier(0.12, 0.9, 0.18, 1.15)",
        easingClose: "cubic-bezier(0.5, 0, 0.75, 0)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        if (phase === "open") {
          return [
            this.kf(0, `translate3d(${v.x}px, ${v.y}px,0) scale(${cfg.enterScale})`),
            this.kf(1, `translate3d(0,0,0) scale(1.02)`),
            this.kf(1, "translate3d(0,0,0) scale(1)"),
          ];
        }
        const v2 = this.vec(side, cfg.exitPx);
        return [
          this.kf(1, "translate3d(0,0,0) scale(1)"),
          this.kf(0, `translate3d(${v2.x}px, ${v2.y}px,0) scale(${cfg.exitScale})`),
        ];
      },
    };

    const bouncy: PresetDef = {
      name: "bouncy",
      defaults: {
        enterPx: 14, exitPx: 10, enterScale: 0.9, exitScale: 0.96,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "anchor",
        easingOpen: "cubic-bezier(0.12, 0.9, 0.18, 1.15)",
        easingClose: "cubic-bezier(0.6, -0.28, 0.74, 0.05)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        if (phase === "open") {
          return [
            this.kf(0, `translate3d(${v.x}px, ${v.y}px,0) scale(${cfg.enterScale})`),
            this.kf(1, "translate3d(0,0,0) scale(1.03)"),
            this.kf(1, "translate3d(0,0,0) scale(1)"),
          ];
        }
        const v2 = this.vec(side, cfg.exitPx);
        return [
          this.kf(1, "translate3d(0,0,0) scale(1)"),
          this.kf(0, `translate3d(${v2.x}px, ${v2.y}px,0) scale(${cfg.exitScale})`),
        ];
      },
    };

    const glide: PresetDef = {
      name: "glide",
      defaults: {
        enterPx: 18, exitPx: 12, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.08, 0.82, 0.17, 1)",
        easingClose: "cubic-bezier(0.3, 0, 1, 1)",
        enterDuration: 220, exitDuration: 200,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        const v2 = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${v.x}px, ${v.y}px,0)`), this.kf(1, "translate3d(0,0,0)")]
          : [this.kf(1, "translate3d(0,0,0)"), this.kf(0, `translate3d(${v2.x}px, ${v2.y}px,0)`)];
      },
    };

    const pop: PresetDef = {
      name: "pop",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 0.85, exitScale: 0.95,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.18, 0.89, 0.32, 1.28)",
        easingClose: "cubic-bezier(0.6, -0.28, 0.74, 0.05)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, cfg }) =>
        phase === "open"
          ? [this.kf(0, `scale(${cfg.enterScale})`), this.kf(1, "scale(1.03)"), this.kf(1, "scale(1)")]
          : [this.kf(1, "scale(1)"), this.kf(0, `scale(${cfg.exitScale})`)],
    };

    const drop: PresetDef = {
      name: "drop",
      defaults: {
        enterPx: 22, exitPx: 14, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "center",
        easingOpen: "cubic-bezier(0.12, 0.9, 0.18, 1)",
        easingClose: "cubic-bezier(0.45, 0, 0.55, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        const v2 = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${v.x}px, ${v.y}px,0)`), this.kf(1, "translate3d(0,0,0)")]
          : [this.kf(1, "translate3d(0,0,0)"), this.kf(0, `translate3d(${v2.x}px, ${v2.y}px,0)`)];
      },
    };

    const grow: PresetDef = {
      name: "grow",
      defaults: {
        enterPx: 6, exitPx: 4, enterScale: 0.95, exitScale: 0.98,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "anchor",
        easingOpen: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        easingClose: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: ({ phase, side, cfg }) => {
        const v = this.vec(side, cfg.enterPx);
        const v2 = this.vec(side, cfg.exitPx);
        return phase === "open"
          ? [this.kf(0, `translate3d(${v.x}px, ${v.y}px,0) scale(${cfg.enterScale})`), this.kf(1, "translate3d(0,0,0) scale(1)")]
          : [this.kf(1, "translate3d(0,0,0) scale(1)"), this.kf(0, `translate3d(${v2.x}px, ${v2.y}px,0) scale(${cfg.exitScale})`)];
      },
    };

    const none: PresetDef = {
      name: "none",
      defaults: {
        enterPx: 0, exitPx: 0, enterScale: 1, exitScale: 1,
        blurInPx: 0, blurOutPx: 0, perspectivePx: 900,
        rotateDeg: 10, tiltDeg: 8, origin: "auto",
        easingOpen: "cubic-bezier(0.16, 1, 0.3, 1)",
        easingClose: "cubic-bezier(0.2, 0, 0, 1)",
        enterDuration: 220, exitDuration: 180,
      },
      getKeyframes: () => [{ opacity: 1 }],
    };

    const floatAlias = floaty;

    return {
      none,
      fade,
      scale,
      slide,
      iosMenu,
      iosSheet,
      materialMenu,
      materialSheet,
      floaty: floatAlias,
      soft,
      sharp,
      blurIn,
      blurFade,
      zoomFromAnchor,
      zoomFromCorner,
      flip3d,
      card3d,
      tilt3d,
      depth3d,
      rotate3d,
      springy,
      bouncy,
      glide,
      pop,
      drop,
      grow,
    };
  }

  // Update the getPresetKeyframes method:
  private getPresetKeyframes(
    preset: PopoverPreset,
    side: PopoverSide,
    phase: "open" | "close",
    overrides?: PopoverPresetConfig,
  ) {
    const reg = this.presetRegistry();
    const def = reg[preset] ?? reg.iosMenu;

    // Merge base defaults with preset defaults, then with overrides
    const baseDefaults = this.getBaseDefaults();
    const cfg = {

      ...def.defaults,
      ...(overrides ?? {})
    } as Required<PopoverPresetConfig>;

    const origin = this.computeOrigin(side, cfg.origin);

    return {
      keyframes: def.getKeyframes({ side, phase, cfg, origin }),
      cfg,
      origin
    };
  }

  // Add this helper method for base defaults:
  private getBaseDefaults(): Required<PopoverPresetConfig> {
    return {
      enterPx: 12,
      exitPx: 8,
      enterScale: 0.98,
      exitScale: 0.99,
      blurInPx: 6,
      blurOutPx: 3,
      perspectivePx: 900,
      rotateDeg: 10,
      tiltDeg: 8,
      origin: "auto",
      easingOpen: "cubic-bezier(0.16, 1, 0.3, 1)",
      easingClose: "cubic-bezier(0.2, 0, 0, 1)",
      enterDuration: 220,
      exitDuration: 180,
    };
  }

  private async playPreset(pop: HTMLElement, side: PopoverSide, phase: "open" | "close") {
    this.checkForConflictingCSS(pop);
    if (this.prefersReducedMotion()) return;

    const preset = this.opts.getPreset();
    if (preset === "none") return;

    // cancel transform-related animations
    this.moveAnim?.cancel();
    this.presetAnim?.cancel();

    this._phase = phase === "open" ? "opening" : "closing";

    pop.style.transition = "none";


    // Get keyframes and config for the current preset
    const { keyframes, cfg, origin } = this.getPresetKeyframes(preset, side, phase);

    // Use duration from config, fallback to controller options
    const duration = phase === "open" ? cfg.enterDuration : cfg.exitDuration;

    const is3d = this.is3dPreset(preset);
    if (is3d) {
      pop.style.transformStyle = "preserve-3d";
      pop.style.backfaceVisibility = "hidden";
      // Perspective lives in the transform; do not set it here.
    } else {
      pop.style.transformStyle = "";
      pop.style.backfaceVisibility = "";
    }
    pop.style.transformOrigin = origin;

    const easing = phase === "open" ? cfg.easingOpen : cfg.easingClose;

    const anim = pop.animate(keyframes as any, {
      duration,
      easing,
      fill: "both",
    });

    this.presetAnim = anim;

    try {
      await anim.finished;
    } catch {
      // cancelled is fine
    } finally {
      if (this.presetAnim === anim) this.presetAnim = undefined;
      pop.style.transition = "";
      if (is3d) {
        pop.style.transformStyle = "";
        pop.style.backfaceVisibility = "";
      }

      this._phase = phase === "open" ? "open" : "idle";
    }
  }

  private checkForConflictingCSS(pop: HTMLElement) {
    const transitions = pop.style.transition || "";

    // Check for conflicting CSS transitions
    if (transitions.includes("transform") && transitions.includes("opacity")) {
      console.warn(
        "Warning: Using 3D animation (WAAPI) alongside CSS transitions on 'transform' or 'opacity' can cause conflicts. Consider removing CSS transitions on these properties."
      );
    }

    if (transitions.includes("margin") || transitions.includes("padding")) {
      console.warn(
        "Warning: Animating 'margin' or 'padding' alongside 3D animation (WAAPI) may cause visual glitches. Please avoid transitioning these properties during WAAPI animations."
      );
    }
  }

  private animateMove(pop: HTMLElement, from: DOMRect, to: DOMRect) {
    if (this._phase !== "open") return;

    const dx = from.left - to.left;
    const dy = from.top - to.top;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

    this.moveAnim?.cancel();

    const duration = this.opts.getRepositionMs();

    const anim = pop.animate(
      [
        { transform: `translate3d(${dx}px, ${dy}px, 0)` },
        { transform: "translate3d(0, 0, 0)" },
      ],
      {
        duration,
        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
        fill: "both",
      },
    );

    this.moveAnim = anim;
    anim.finished.finally(() => {
      if (this.moveAnim === anim) this.moveAnim = undefined;
    });
  }

  private is3dPreset(preset: PopoverPreset) {
    return (
      preset === "flip3d" ||
      preset === "card3d" ||
      preset === "tilt3d" ||
      preset === "depth3d" ||
      preset === "rotate3d"
    );
  }

  private setSideAttr(side: PopoverSide) {
    const pop = this.opts.getPopover();
    if (!pop) return;
    pop.setAttribute(this.opts.sideAttr, side);
  }

  private setupObserversAndScrollParents() {
    if (typeof window === "undefined") return;

    const anchor = this.opts.getAnchor();
    const pop = this.opts.getPopover();

    if (anchor && "ResizeObserver" in window) {
      this.anchorRO?.disconnect();
      this.anchorRO = new ResizeObserver(() => {
        if (this._open) this.position();
      });
      this.anchorRO.observe(anchor);
    }

    if (pop && "ResizeObserver" in window) {
      this.popoverRO?.disconnect();
      this.popoverRO = new ResizeObserver(() => {
        if (this._open) this.position();
      });
      this.popoverRO.observe(pop);
    }

    this.teardownScrollParents();

    const mode = this.opts.scrollContainer;
    if (mode === "window") return;

    if (mode instanceof HTMLElement) {
      this.bindScroll(mode);
      return;
    }

    if (anchor) {
      for (const p of this.getScrollParents(anchor)) this.bindScroll(p);
    }
  }

  private bindScroll(el: HTMLElement) {
    const fn = this.onViewportMove as EventListener;
    el.addEventListener("scroll", fn, { passive: true });
    this.boundScrollParents.push({ el, fn });
  }

  private teardownScrollParents() {
    for (const { el, fn } of this.boundScrollParents) {
      (el as any).removeEventListener?.("scroll", fn);
    }
    this.boundScrollParents = [];
  }

  private getScrollParents(node: HTMLElement): HTMLElement[] {
    const parents: HTMLElement[] = [];
    let el: HTMLElement | null = node.parentElement;

    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;

      const canScrollY =
        (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
        el.scrollHeight > el.clientHeight;

      const canScrollX =
        (overflowX === "auto" || overflowX === "scroll" || overflowX === "overlay") &&
        el.scrollWidth > el.clientWidth;

      if (canScrollY || canScrollX) parents.push(el);
      el = el.parentElement;
    }

    return parents;
  }

  private onViewportMove = () => {
    if (this._open) this.position();
  };

  private onKeydown = (e: KeyboardEvent) => {
    if (!this._open) return;
    if (e.key !== "Escape") return;

    if (!this.opts.getCloseOnEscape()) return;

    const pop = this.opts.getPopover();
    const anchor = this.opts.getAnchor();
    const active = document.activeElement;

    const withinPopover = !!(pop && active && pop.contains(active));
    const onAnchor = !!(anchor && active && anchor.contains(active));

    if (withinPopover || onAnchor) {
      e.preventDefault();
      e.stopPropagation();
      this.closePopover("escape");
    }
  };

  private onGroupOpen = (e: CustomEvent) => {
    const other = e.detail?.id;
    const mine = this.opts.groupId ?? this.host;

    if (other !== mine && this._open) {
      this.closePopover("group");
    }
  };

  private captureFocusBeforeOpen() {
    this.lastFocusedEl = document.activeElement;
  }

  private elementIsTriggerRelated(el: EventTarget | null | undefined): boolean {
    if (!(el instanceof HTMLElement)) return false;

    const anchor = this.opts.getAnchor();
    const focusTarget = this.opts.getFocusTarget?.() ?? null;

    if (focusTarget && (el === focusTarget || focusTarget.contains(el))) {
      return true;
    }
    if (!anchor) return false;
    if (el === anchor || anchor.contains(el)) return true;
    if (anchor.shadowRoot?.contains(el)) return true;
    return false;
  }

  private restoreFocus() {
    if (!this.opts.getRestoreFocusOnClose()) return;

    const anchor = this.opts.getAnchor();
    if (!anchor) return;

    const focusTarget = this.opts.getFocusTarget?.() ?? anchor;
    const active = document.activeElement;
    const last = this.lastFocusedEl;
    const pop = this.opts.getPopover();

    const lastWasInsidePopover = !!(pop && last && pop.contains(last));
    const lastWasInsideTrigger = this.elementIsTriggerRelated(last);
    const activeInsidePopover = !!(pop && active && pop.contains(active));

    if (activeInsidePopover || lastWasInsidePopover || lastWasInsideTrigger) {
      focusTarget.focus?.({ preventScroll: true });
    }
  }

  private ensurePopoverMarginZero() {
    const pop = this.opts.getPopover();
    if (!pop) return;
    if (!pop.style.margin) pop.style.margin = "0";
  }

  /**
   * Utility method to manually update a specific option
   * (Useful for edge cases where you need direct control)
   */
  updateOption<T extends keyof typeof this.opts>(
    key: T,
    value: typeof this.opts[T]
  ) {
    if (typeof value === "function") {
      // For getters, replace the function
      (this.opts as any)[key] = value;
    } else {
      // For other values, wrap in a getter
      (this.opts as any)[key] = () => value;
    }

    if (this._open && key.startsWith("get")) {
      // Reposition if open and a positioning property changed
      this.position();
    }
  }
}